import handleOnboardingChecklistEvent from '@/redux/actions/pieveActions/lib/handleOnboardingChecklistEvent.js';
import isSkillKnowledgePath from '@/lib/isSkillKnowledgePath.js';
import { isWorkflowPlanKnowledgePath } from '@shared/pieve/workflowPlanKnowledgePath.js';
import toPieveEquivalentToolCall from '@shared/pieve/toPieveEquivalentToolCall.js';

/* Which reads a workflow tool's result makes stale, keyed by tool, each key named for the resource it refreshes.
   Step mutations refetch the authoritative draft even if their workflow.updated push was missed.
   Snapshot revision ordering prevents a delayed read from overwriting a newer push.

   - workflow_create_from_thread: the draft first exists. The plan path, the connection source and the actions
     owner all move with the workflow id it brings, so those reads follow on their own.
   - workflow_publish: a new version — the version list, the published snapshot and the webhook binding. The draft
     is pushed.
   - workflow_plan_amendment_propose: the server rewrites the plan itself when an amendment is approved, so no
     knowledge_write reaches the stream for it, and it mirrors the amendment into the actions list.
   - workflow_plan_manifest_update: the plan's declared apps, which the connections section shows while planning. */
const WORKFLOW_TOOL_EVENT_KEYS = {
  workflow_create_from_thread: ['workflowDraftUpdated'],
  workflow_step_create: ['workflowDraftUpdated'],
  workflow_steps_create: ['workflowDraftUpdated'],
  workflow_step_update: ['workflowDraftUpdated'],
  workflow_step_delete: ['workflowDraftUpdated'],
  workflow_step_move: ['workflowDraftUpdated'],
  workflow_step_reorder: ['workflowDraftUpdated'],
  workflow_step_resolve_app_version: ['workflowDraftUpdated'],
  workflow_publish: ['workflowPublished'],
  workflow_actions_needed_update: ['workflowActionsUpdated'],
  workflow_plan_amendment_propose: ['workflowPlanUpdated', 'workflowActionsUpdated'],
  workflow_plan_manifest_update: ['workflowPlanUpdated']
};

const handleToolResultEvent = ({ rawEvent, dispatch }) => {
  const rawPayload = rawEvent?.payload && typeof rawEvent.payload === 'object' ? rawEvent.payload : {};
  // A plan run names Pieve's tools `mcp__pieve__<tool>` and stores their result as text; read both as Pieve's own, so
  // the panels refresh the same whichever lane ran the tool (passthrough L45).
  const { toolName: rawToolName } = toPieveEquivalentToolCall({ toolName: String(rawPayload?.toolName || rawPayload?.name || '').trim(), harness: rawPayload?.harness });
  const output = rawPayload?.output;
  const rawToolPath = String(output?.path || output?.data?.path || rawPayload?.result?.path || '').trim();

  if (rawToolName === 'knowledge_write' && isSkillKnowledgePath(rawToolPath)) {
    dispatch({ type: 'uiSetEventKey', payload: { key: 'skillUpdated' } });
  }

  // The agent authors the plan with knowledge_write, at the pre-draft thread-staged path and at the canonical
  // workflow path. No workflow tool fires for either, so this is the plan's only refresh signal.
  if (rawToolName === 'knowledge_write' && isWorkflowPlanKnowledgePath(rawToolPath)) {
    dispatch({ type: 'uiSetEventKey', payload: { key: 'workflowPlanUpdated' } });
  }

  for (const key of WORKFLOW_TOOL_EVENT_KEYS[rawToolName] || []) {
    dispatch({ type: 'uiSetEventKey', payload: { key } });
  }

  if (rawToolName === 'run_workflow') {
    dispatch({ type: 'uiSetEventKey', payload: { key: 'workflowRun' } });
  }

  handleOnboardingChecklistEvent({ rawToolName, rawToolPath });
};

export default handleToolResultEvent;
